/**
 * NFL RTDB Cache Manager
 * Handles Firebase Realtime Database operations for NFL data caching
 * Adapted from football RTDB implementation with NFL-specific optimizations
 */

import { initializeApp } from 'firebase/app';
import { 
  getDatabase, 
  ref, 
  get, 
  set, 
  onValue, 
  off,
  serverTimestamp 
} from 'firebase/database';
import { 
  NFL_RTDB_URL, 
  CACHE_PATHS, 
  logCache, 
  shouldUseCache,
  getTTLForGame,
  TTL_CONFIG
} from './rtdbConfig';

class NFLRTDBCache {
  constructor() {
    this.db = null;
    this.app = null;
    this.subscriptions = new Map();
    this.connectionStatus = 'disconnected';
    this.statsUpdateInterval = null;
    this.localStats = {
      hits: 0,
      misses: 0,
      sets: 0,
      errors: 0
    };
    
    this.initializeFirebase();
  }

  initializeFirebase() {
    try {
      // Initialize Firebase app for NFL RTDB
      const config = {
        databaseURL: NFL_RTDB_URL
      };
      
      // Create a unique app name to avoid conflicts with football RTDB
      this.app = initializeApp(config, 'nfl-rtdb-app');
      this.db = getDatabase(this.app);
      
      // Monitor connection status
      const connectedRef = ref(this.db, '.info/connected');
      onValue(connectedRef, (snapshot) => {
        const connected = snapshot.val();
        this.connectionStatus = connected ? 'connected' : 'disconnected';
        logCache('info', `RTDB connection status: ${this.connectionStatus}`);
      });
      
      // Start stats update interval
      this.startStatsUpdater();
      
      logCache('info', 'NFL RTDB Cache initialized successfully');
    } catch (error) {
      logCache('error', 'Failed to initialize NFL RTDB', error);
      this.connectionStatus = 'error';
    }
  }

  /**
   * Get data from cache
   * @param {string} path - Cache path
   * @returns {Promise<any>} Cached data or null
   */
  async get(path) {
    if (!this.db) {
      logCache('error', 'RTDB not initialized');
      return null;
    }

    try {
      const dataRef = ref(this.db, path);
      const snapshot = await get(dataRef);
      
      if (!snapshot.exists()) {
        this.localStats.misses++;
        logCache('miss', `Cache miss: ${path}`);
        return null;
      }
      
      const data = snapshot.val();
      
      // Check if cache is still valid
      if (!shouldUseCache(data)) {
        this.localStats.misses++;
        logCache('miss', `Cache expired: ${path}`, {
          age: Date.now() - data.lastUpdated,
          ttl: data.ttl
        });
        return null;
      }
      
      // Update hit counter
      this.localStats.hits++;
      this.incrementHitCount(path);
      
      logCache('hit', `Cache hit: ${path}`, {
        age: Date.now() - data.lastUpdated,
        ttl: data.ttl
      });
      
      return data.data;
    } catch (error) {
      this.localStats.errors++;
      logCache('error', `Cache get error: ${path}`, error);
      return null;
    }
  }

  /**
   * Set data in cache with appropriate TTL
   * @param {string} path - Cache path
   * @param {any} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds
   * @returns {Promise<boolean>} Success status
   */
  async set(path, data, ttl = TTL_CONFIG.DEFAULT) {
    if (!this.db) {
      logCache('error', 'RTDB not initialized');
      return false;
    }

    try {
      const dataRef = ref(this.db, path);
      const cacheEntry = {
        data,
        ttl,
        lastUpdated: Date.now(),
        hits: 0
      };
      
      await set(dataRef, cacheEntry);
      this.localStats.sets++;
      
      logCache('set', `Cache set: ${path}`, {
        ttl,
        dataSize: JSON.stringify(data).length
      });
      
      return true;
    } catch (error) {
      this.localStats.errors++;
      logCache('error', `Cache set error: ${path}`, error);
      return false;
    }
  }

  /**
   * Subscribe to real-time updates for a cache path
   * @param {string} path - Cache path
   * @param {Function} callback - Callback for updates
   * @returns {Function} Unsubscribe function
   */
  subscribe(path, callback) {
    if (!this.db) {
      logCache('error', 'RTDB not initialized');
      return () => {};
    }

    try {
      const dataRef = ref(this.db, path);
      
      // Create subscription
      const listener = onValue(dataRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          
          // Check if cache is still valid
          if (shouldUseCache(data)) {
            callback(data.data);
          } else {
            // Cache expired, notify with null
            callback(null);
          }
        } else {
          // No data exists
          callback(null);
        }
      });
      
      // Store subscription
      const subscriptionId = `${path}_${Date.now()}`;
      this.subscriptions.set(subscriptionId, { path, listener, dataRef });
      
      logCache('info', `Subscribed to: ${path}`);
      
      // Return unsubscribe function
      return () => {
        off(dataRef, 'value', listener);
        this.subscriptions.delete(subscriptionId);
        logCache('info', `Unsubscribed from: ${path}`);
      };
    } catch (error) {
      logCache('error', `Subscribe error: ${path}`, error);
      return () => {};
    }
  }

  /**
   * Clear all cache or specific path
   * @param {string} path - Optional specific path to clear
   */
  async clear(path = null) {
    if (!this.db) {
      logCache('error', 'RTDB not initialized');
      return false;
    }

    try {
      const targetPath = path || CACHE_PATHS.ROOT;
      const dataRef = ref(this.db, targetPath);
      await set(dataRef, null);
      
      logCache('info', `Cache cleared: ${targetPath}`);
      return true;
    } catch (error) {
      logCache('error', `Clear cache error: ${path}`, error);
      return false;
    }
  }

  /**
   * Get cache statistics
   */
  async getStats() {
    try {
      // Get global stats from RTDB
      const statsRef = ref(this.db, CACHE_PATHS.STATS);
      const snapshot = await get(statsRef);
      const globalStats = snapshot.exists() ? snapshot.val() : {};
      
      return {
        local: this.localStats,
        global: globalStats,
        connectionStatus: this.connectionStatus,
        subscriptions: this.subscriptions.size
      };
    } catch (error) {
      logCache('error', 'Failed to get stats', error);
      return {
        local: this.localStats,
        connectionStatus: this.connectionStatus,
        subscriptions: this.subscriptions.size
      };
    }
  }

  /**
   * Increment hit count for a cache entry
   */
  async incrementHitCount(path) {
    try {
      const hitsRef = ref(this.db, `${path}/hits`);
      const snapshot = await get(hitsRef);
      const currentHits = snapshot.exists() ? snapshot.val() : 0;
      await set(hitsRef, currentHits + 1);
    } catch (error) {
      // Silent fail - not critical
    }
  }

  /**
   * Update global statistics periodically
   */
  startStatsUpdater() {
    // Update stats every 5 minutes
    this.statsUpdateInterval = setInterval(async () => {
      try {
        const statsRef = ref(this.db, CACHE_PATHS.STATS);
        const snapshot = await get(statsRef);
        const currentStats = snapshot.exists() ? snapshot.val() : {
          totalHits: 0,
          totalMisses: 0,
          totalSets: 0,
          apiCallsSaved: 0
        };
        
        // Update with local stats
        const updatedStats = {
          totalHits: (currentStats.totalHits || 0) + this.localStats.hits,
          totalMisses: (currentStats.totalMisses || 0) + this.localStats.misses,
          totalSets: (currentStats.totalSets || 0) + this.localStats.sets,
          apiCallsSaved: (currentStats.apiCallsSaved || 0) + this.localStats.hits,
          lastUpdated: Date.now()
        };
        
        await set(statsRef, updatedStats);
        
        // Reset local stats
        this.localStats = {
          hits: 0,
          misses: 0,
          sets: 0,
          errors: 0
        };
      } catch (error) {
        // Silent fail - not critical
      }
    }, 5 * 60 * 1000); // 5 minutes
  }

  /**
   * Check if connected to RTDB
   */
  isConnected() {
    return this.connectionStatus === 'connected';
  }

  /**
   * Clean up resources
   */
  destroy() {
    // Clear all subscriptions
    this.subscriptions.forEach(({ dataRef, listener }) => {
      off(dataRef, 'value', listener);
    });
    this.subscriptions.clear();
    
    // Clear stats interval
    if (this.statsUpdateInterval) {
      clearInterval(this.statsUpdateInterval);
    }
    
    logCache('info', 'NFL RTDB Cache destroyed');
  }

  /**
   * Special method for caching game collections with smart TTL
   */
  async setGames(path, games, defaultTTL = TTL_CONFIG.DATE_GAMES) {
    if (!games || !Array.isArray(games)) {
      return false;
    }

    // Determine TTL based on game statuses
    let ttl = defaultTTL;
    
    // Check if any games are live
    const hasLiveGames = games.some(game => {
      const status = game?.game?.status?.short || '';
      return ['Q1', 'Q2', 'Q3', 'Q4', 'OT', 'HT', 'LIVE'].includes(status);
    });
    
    if (hasLiveGames) {
      ttl = TTL_CONFIG.LIVE_GAMES; // 30 seconds for live games
    } else {
      // Check if all games are completed
      const allCompleted = games.every(game => {
        const status = game?.game?.status?.short || '';
        return ['FT', 'AOT', 'CANC', 'PST'].includes(status);
      });
      
      if (allCompleted) {
        // Check if games are from today
        const today = new Date().toISOString().split('T')[0];
        const fromToday = games.some(game => 
          game?.game?.date?.date === today
        );
        
        ttl = fromToday ? TTL_CONFIG.PAST_GAMES : TTL_CONFIG.HISTORICAL_GAMES;
      }
    }
    
    return await this.set(path, games, ttl);
  }

  /**
   * Warm cache with popular data
   */
  async warmCache(weekId, season, teams = []) {
    try {
      logCache('info', `Warming cache for week ${weekId}, season ${season}`);
      
      // This would be called by a background process
      // Implementation depends on having access to NFLService
      
      return true;
    } catch (error) {
      logCache('error', 'Cache warming failed', error);
      return false;
    }
  }
}

// Create singleton instance
const nflRTDBCache = new NFLRTDBCache();

// Export instance and methods
export default nflRTDBCache;

// Named exports for convenience
export const {
  get: getCached,
  set: setCached,
  subscribe: subscribeToCache,
  clear: clearCache,
  getStats: getCacheStats,
  isConnected: isCacheConnected,
  setGames: cacheGames,
  warmCache
} = nflRTDBCache;

// Browser debugging helper
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.NFLRTDBCache = {
    instance: nflRTDBCache,
    getStats: () => nflRTDBCache.getStats(),
    clear: (path) => nflRTDBCache.clear(path),
    isConnected: () => nflRTDBCache.isConnected()
  };
}